import { NavigateFunction, useNavigate } from 'react-router-dom';
import { useLoginMutation } from 'src/api/endpoints/auth/api';
import { notifyAboutException } from 'src/utils/notify';
import { requireField } from 'src/constants/constants';
import { InputText } from 'primereact/inputtext';
import { FormikProps, useFormik } from 'formik';
import { classNames } from 'primereact/utils';
import { Helmet } from 'react-helmet-async';
import { Button } from 'primereact/button';
import logo from '../../static/logo.svg';
import { ResponseError } from 'src/api';
import { FC, useState } from 'react';
import './AuthPage.scss';
import { LOGIN_REGEX } from 'src/pages/edu/Students';

export const AuthPage: FC = () => {
    const [buttonLoading, setButtonLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const navigate: NavigateFunction = useNavigate();

    const [login] = useLoginMutation();

    const formik: FormikProps<any> = useFormik({
        initialValues: {
            login: '',
            password: '',
        },
        validateOnMount: true,
        validate({ login, password }) {
            const errors: {
                login?: string;
                password?: string;
            } = {};

            if (!login.trim().length) {
                errors.login = requireField;
            } else if (!login.trim().match(LOGIN_REGEX)) {
                errors.login = 'Неверный формат логина';
            }

            if (!password.trim().length) errors.password = requireField;

            return errors;
        },
        onSubmit: async (data): Promise<void> => {
            setButtonLoading(true);

            await login({
                body: { username: data.login, password: data.password },
            })
                .unwrap()
                .then(async (response): Promise<void> => {
                    localStorage.setItem('access-token', response.accessToken);
                    localStorage.setItem(
                        'refresh-token',
                        response.refreshToken
                    );
                    localStorage.setItem('userId', response.userId);

                    setButtonLoading(false);
                    navigate('/');
                })
                .catch((error: ResponseError) => {
                    setButtonLoading(false);

                    formik.setFieldValue('password', '');

                    notifyAboutException({
                        content: error.message,
                        sound: true,
                    });
                });
        },
    });

    const isFormFieldValid = (name: 'login' | 'password') =>
        !!(formik.touched[name] && formik.errors[name]);

    const toggleVisible = () => setVisible(prev => !prev);

    return (
        <>
            <Helmet title={'Авторизация'} />
            <div className="w-full container flex align-items-center justify-content-center auth-page">
                <form
                    onSubmit={formik.handleSubmit}
                    className="flex flex-column align-items-center form"
                >
                    <div className="logo">
                        <img src={logo} alt="logo" />
                    </div>

                    <div className="field first">
                        <span className="p-float-label">
                            <InputText
                                id="login"
                                value={formik.values.login}
                                onChange={formik.handleChange}
                                autoFocus={true}
                                className={
                                    classNames({
                                        'p-invalid': isFormFieldValid('login'),
                                    }) + ' p-inputtext-lg block'
                                }
                            />
                            <label
                                htmlFor="login"
                                className={classNames({
                                    'p-error': isFormFieldValid('login'),
                                })}
                            >
                                Логин
                            </label>
                        </span>
                    </div>
                    <div className="field flex flex-column">
                        <span className="p-float-label p-input-icon-right">
                            {visible ? (
                                <i
                                    className="pi pi-eye"
                                    onClick={toggleVisible}
                                />
                            ) : (
                                <i
                                    className="pi pi-eye-slash"
                                    onClick={toggleVisible}
                                />
                            )}
                            <InputText
                                id="password"
                                type={visible ? 'text' : 'password'}
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                className={
                                    classNames({
                                        'p-invalid':
                                            isFormFieldValid('password'),
                                    }) + ' p-inputtext-lg block'
                                }
                            />
                            <label
                                htmlFor="password"
                                className={classNames({
                                    'p-error': isFormFieldValid('password'),
                                })}
                            >
                                Пароль
                            </label>
                        </span>
                    </div>

                    <Button
                        type="submit"
                        loading={buttonLoading}
                        disabled={!formik.isValid}
                    >
                        Войти
                    </Button>
                </form>
            </div>
        </>
    );
};
